import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckOutlined, LoadingOutlined } from "@ant-design/icons";
import { Button, Input, Select } from "antd";
import { AlertMessage, AutoModel } from "../../components/common";
import { useAlert } from "../../hooks/useAlert";
import { ALERT_Z_INDEX } from "../../utils/alertManager";
import { getDictDataListSimpleApi, getTargetLanguageDictListSimpleApi } from "../../service/dict";
import {
  getTimbreDesignTemplateApi,
  postTimbreDesignGenerateApi,
  postTimbreDesignSaveApi,
  resolveTimbreDesignPreviewUrl,
} from "../../service/timbreDesign";
import { VIDEO_CDN_PREFIX } from "../../config/video";
import {
  findFirstResolvableLangValueMatchingText,
  validatePreviewTextForLang,
} from "../../utils/voicePreviewLangValidate";
import { audioManager } from "../../utils/audioManager";
import { triggerBlobDownload } from "../../utils/batchVideoDownload";
import { CoinsEstimateIcon } from "../../components/common/CoinsEstimateIcon";
import { useHasPermission } from "../../hooks/useHasPermission";
import { PERMISSIONS } from "../../constants/permissionKeys";
import { triggerPointsRefresh } from "../../service/points";
import { triggerMembershipRefresh } from "../../service/membership";
import "./VoiceDesignWorkspacePage.scss";

/** 与 doc/7 `data-lucide="sparkles"` 一致（Lucide sparkles 轮廓） */
const SparklesIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M5 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 5h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 17v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17 19h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** 黑底圆钮内：空心三角（描边、不填充） */
const VoicePreviewPlayIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M9 6.5 17 12 9 17.5z"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);


const DownloadIcon: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const RefreshCwIcon: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M21 12a9 9 0 0 0-15.31-6.36L3 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 3v5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 12a9 9 0 0 0 15.31 6.36L21 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 16h5v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckCircleIcon: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="m9 11 3 3L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckboxCheckIcon: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LightbulbIcon: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M15 14c.2-1 .7-1.7 1.5-2.5A4.8 4.8 0 0 0 18 8 6 6 0 0 0 6 8c0 1.3.5 2.5 1.5 3.5.8.8 1.3 1.5 1.5 2.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 18h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M10 22h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ChevronDownIcon: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BookOpenIcon: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MegaphoneIcon: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="m3 11 18-5v12L3 14z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const VoicePreviewPauseIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M8 7v10M16 7v10"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
    />
  </svg>
);

const DEFAULT_TEST_TEXT =
  "科技的浪潮正在重塑我们的世界，而声音，是传递情感最温情的介质。";

/** 试听文本变化后延迟检测语种，避免输入过程中频繁切换 */
const PREVIEW_LANG_DEBOUNCE_MS = 450;
type VoiceDesignTemplate = {
  id: number | string;
  title: string;
  description: string;
  voiceDescription: string;
  previewText: string;
  icon?: "book" | "sparkles" | "megaphone";
};

const FALLBACK_VOICE_DESIGN_TEMPLATES = [
  {
    id: "documentary",
    title: "纪录片旁白",
    description: "沉稳、清晰、可信赖，适合长内容叙事。",
    voiceDescription: "一位成熟稳重的纪录片男旁白，普通话标准，语速平缓，声音厚实但不压迫，适合讲述历史、人文和自然题材。",
    previewText: "山河辽阔，时间在大地上留下沉静的纹理，也留下人们与世界相处的方式。",
    icon: "book",
  },
  {
    id: "children",
    title: "少儿故事声",
    description: "温暖、轻快、有亲和力，适合故事表达。",
    voiceDescription: "一位亲切明亮的年轻女声，透气自然有笑意，节奏轻快，适合少儿故事、知识科普和轻松讲解。",
    previewText: "小小的种子醒来了，它伸了伸懒腰，悄悄把第一片嫩芽送出了泥土。",
    icon: "sparkles",
  },
  {
    id: "brand",
    title: "品牌广告声",
    description: "专业、干净、有节奏，适合商业口播。",
    voiceDescription: "一位干净利落的品牌广告男声，普通话标准，语气自信克制，节奏明快，适合产品介绍和品牌片。",
    previewText: "从今天开始，让每一次创作都更高效，也让每一个声音都更接近真实表达。",
    icon: "megaphone",
  },
] satisfies VoiceDesignTemplate[];

type GeneratedVoiceRow = TimbreDesign.GeneratedEntry & {
  /** 规范化后的试听 URL（来自 audioUrl 或 audioPath） */
  audioUrl: string;
  timbreId: string;
  description: string;
  originText: string;
  indexLabel: string;
  durationLabel: string;
  wave: number[];
};

function waveBarsFromSeed(seed: string): number[] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) | 0;
  }
  const out: number[] = [];
  for (let i = 0; i < 25; i++) {
    h = (h * 1103515245 + 12345) | 0;
    out.push(12 + (Math.abs(h) % 21));
  }
  return out;
}

/** 无 timbreId 时生成稳定卡片 id（避免 key 冲突、播放态串台） */
function stableGenRowId(audioUrl: string, idx: number): string {
  let h = 0;
  const s = audioUrl || String(idx);
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return `gen-${idx}-${Math.abs(h).toString(36)}`;
}

function mapGenerateList(data: TimbreDesign.GeneratedEntry[], fallbackDescription = ""): GeneratedVoiceRow[] {
  return data.map((item, idx) => {
    const audioUrl = (item.audioUrl ?? item.audioPath ?? "").trim();
    const timbreId = (item.timbreId ?? "").trim() || stableGenRowId(audioUrl, idx);
    const description = (item.description ?? fallbackDescription).trim();
    const originText = (item.originText ?? "").trim();
    return {
      ...item,
      audioUrl,
      timbreId,
      description,
      originText,
      indexLabel: `音色 #${idx + 1}`,
      durationLabel: "—",
      wave: waveBarsFromSeed(timbreId),
    };
  });
}

function disposePreviewAudio(el: HTMLAudioElement | null) {
  if (!el) return;
  el.onended = null;
  el.onerror = null;
  try {
    el.pause();
    el.currentTime = 0;
    el.removeAttribute("src");
    el.load();
  } catch {
    // ignore
  }
}


const dictToSelectOptions = (items: Dict.DictItem[]) =>
  items.map((d) => ({ value: d.value, label: d.name }));

/**
 * doc/7.音色设计页 主内容区（332–755）
 */
export const VoiceDesignWorkspacePage: FC = () => {
  const { alertConfig, hideAlert, showSuccess, showError, showWarning, showInfo } = useAlert();
  const hasPoints = useHasPermission(PERMISSIONS.POINTS_VIEW) === true;
  const [testText, setTestText] = useState(DEFAULT_TEST_TEXT);
  const [voiceDescription, setVoiceDescription] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [generatedVoices, setGeneratedVoices] = useState<GeneratedVoiceRow[]>([]);
  const [selectedTimbreId, setSelectedTimbreId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const generateStartTimeRef = useRef<number | null>(null);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [voiceName, setVoiceName] = useState("");
  const [modalLang, setModalLang] = useState<string | undefined>();
  const [modalGender, setModalGender] = useState<string | undefined>();
  const [modalAge, setModalAge] = useState<string | undefined>();
  const [modalCategory, setModalCategory] = useState<string | undefined>();
  const [modalDesc, setModalDesc] = useState("");
  const [saveSubmitting, setSaveSubmitting] = useState(false);
  const [examplesCollapsed, setExamplesCollapsed] = useState(showResults);
  const [voiceTemplates, setVoiceTemplates] = useState<VoiceDesignTemplate[]>(FALLBACK_VOICE_DESIGN_TEMPLATES);

  const [langOptions, setLangOptions] = useState<Dict.DictItem[]>([]);
  const [genderOptions, setGenderOptions] = useState<Dict.DictItem[]>([]);
  const [ageOptions, setAgeOptions] = useState<Dict.DictItem[]>([]);
  const [timbreOptions, setTimbreOptions] = useState<Dict.DictItem[]>([]);


  const [playingTimbreId, setPlayingTimbreId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const CDN_PREFIX = VIDEO_CDN_PREFIX;

  useEffect(() => {
    let cancelled = false;
    const fetchTemplates = async () => {
      try {
        const res = await getTimbreDesignTemplateApi();
        if (cancelled || res.code !== 0 || !Array.isArray(res.data)) return;
        const templates = res.data
          .map((item, index): VoiceDesignTemplate | null => {
            const title = (item.title ?? "").trim();
            const description = (item.description ?? "").trim();
            const voiceDescription = (item.voiceDescription ?? "").trim();
            const previewText = (item.previewText ?? "").trim();
            if (!title || !voiceDescription || !previewText) return null;
            return {
              id: item.id ?? `remote-${index}`,
              title,
              description,
              voiceDescription,
              previewText,
              icon: index === 0 ? "book" : index === 2 ? "megaphone" : "sparkles",
            };
          })
          .filter((item): item is VoiceDesignTemplate => Boolean(item));
        if (templates.length) setVoiceTemplates(templates);
      } catch {
        // 模板接口不可用时保留本地兜底示例，避免影响页面主流程。
      }
    };
    void fetchTemplates();
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    const fetchDicts = async () => {
      try {
        const [langRes, genderRes, ageRes, timbreRes] = await Promise.all([
          getTargetLanguageDictListSimpleApi(),
          getDictDataListSimpleApi("system_user_sex"),
          getDictDataListSimpleApi("age_category"),
          getDictDataListSimpleApi("voice_type"),
        ]);
        if (langRes.code === 0 && Array.isArray(langRes.data)) setLangOptions(langRes.data);
        if (genderRes.code === 0 && Array.isArray(genderRes.data)) setGenderOptions(genderRes.data);
        if (ageRes.code === 0 && Array.isArray(ageRes.data)) setAgeOptions(ageRes.data);
        if (timbreRes.code === 0 && Array.isArray(timbreRes.data)) {
          setTimbreOptions(timbreRes.data);
          if (timbreRes.data.length > 0 && !modalCategory) {
            setModalCategory(String(timbreRes.data[0].value));
          }
        }
      } catch (error) {
        console.error("获取字典数据失败:", error);
      }
    };
    void fetchDicts();
  }, []);




  /** 登记弹窗：试听文本防抖 + 语种与正文校对（与主表单同一套 target_language + validate） */
  useEffect(() => {
    if (!registerOpen || !langOptions.length || !modalLang) return;
    const trimmed = modalDesc.trim();
    const timer = window.setTimeout(() => {
      if (!trimmed) return;
      const ok = validatePreviewTextForLang(trimmed, modalLang, langOptions);
      if (ok.ok) return;
      const fix = findFirstResolvableLangValueMatchingText(trimmed, langOptions);
      if (!fix || fix === modalLang) return;
      const label = langOptions.find((d) => String(d.value) === fix)?.name?.trim() ?? fix;
      setModalLang(fix);
      showInfo(`检测到当前输入文本为「${label}」`);
    }, PREVIEW_LANG_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [modalDesc, modalLang, langOptions, registerOpen, showInfo]);

  useEffect(() => {
    return () => {
      const el = audioRef.current;
      disposePreviewAudio(el);
      audioRef.current = null;
      setPlayingTimbreId(null);
      audioManager.stopAll();
    };
  }, []);

  const selectedLabel = useMemo(() => {
    if (!selectedTimbreId) return "未选择";
    const v = generatedVoices.find((x) => x.timbreId === selectedTimbreId);
    return v?.indexLabel ?? "未选择";
  }, [selectedTimbreId, generatedVoices]);

  const stopPreview = useCallback(() => {
    const el = audioRef.current;
    disposePreviewAudio(el);
    audioRef.current = null;
    setPlayingTimbreId(null);
  }, []);

  const handleDownload = useCallback(
    async (e: React.MouseEvent, row: GeneratedVoiceRow) => {
      e.stopPropagation();
      const fullUrl = resolveTimbreDesignPreviewUrl(row.audioUrl, CDN_PREFIX);
      if (!fullUrl) {
        showError("暂无音频可下载");
        return;
      }
      const safeName = (row.indexLabel || "音色").replace(/[<>:"/\\|?*]/g, "_");
      const extMatch = fullUrl.match(/\.(wav|mp3|m4a|ogg|aac)(?:\?|$)/i);
      const ext = extMatch ? `.${extMatch[1].toLowerCase()}` : ".wav";
      try {
        const res = await fetch(fullUrl, { cache: "no-store", mode: "cors" });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const blob = await res.blob();
        if (!blob.size) {
          throw new Error("empty file");
        }
        triggerBlobDownload(blob, `${safeName}${ext}`);
      } catch {
        showError("下载失败，请稍后重试");
      }
    },
    [CDN_PREFIX, showError]
  );

  const togglePlayPreview = useCallback(
    (row: GeneratedVoiceRow) => {
      const fullUrl = resolveTimbreDesignPreviewUrl(row.audioUrl, CDN_PREFIX);
      if (!fullUrl) {
        showError("暂无试听地址");
        return;
      }

      if (playingTimbreId === row.timbreId) {
        stopPreview();
        return;
      }

      const prev = audioRef.current;
      if (prev) {
        disposePreviewAudio(prev);
        if (audioRef.current === prev) {
          audioRef.current = null;
        }
      }

      audioManager.stopAll();

      try {
        const audio = new Audio(fullUrl);
        const instance = audio;
        audioRef.current = audio;

        audio.onended = () => {
          if (audioRef.current !== instance) return;
          setPlayingTimbreId(null);
          audioRef.current = null;
        };

        audio.onerror = () => {
          if (audioRef.current !== instance) return;
          showError("音频播放失败");
          setPlayingTimbreId(null);
          audioRef.current = null;
        };

        void audio.play().catch((err) => {
          if (audioRef.current !== instance) return;
          console.error("播放音频出错:", err);
          showError("音频播放失败");
          setPlayingTimbreId(null);
          audioRef.current = null;
        });
        setPlayingTimbreId(row.timbreId);
      } catch (error) {
        console.error("播放音频出错:", error);
        showError("播放失败，请重试");
        setPlayingTimbreId(null);
        audioRef.current = null;
      }
    },
    [CDN_PREFIX, playingTimbreId, showError, stopPreview]
  );

  /** 登记弹窗内切换语种：与弹窗试听文本校对（逻辑同主表单） */
  const handleModalLangChange = useCallback(
    (v: string | number | null | undefined) => {
      const raw = v == null ? "" : String(v);
      if (!raw.trim()) {
        setModalLang(undefined);
        return;
      }
      const next = raw.trim();
      setModalLang(next);
      if (!langOptions.length) return;
      const trimmed = modalDesc.trim();
      if (!trimmed) return;
      const langMatch = validatePreviewTextForLang(trimmed, next, langOptions);
      if (langMatch.ok) return;
      const fix = findFirstResolvableLangValueMatchingText(trimmed, langOptions);
      if (fix && fix !== next) {
        setModalLang(fix);
        const label = langOptions.find((d) => String(d.value) === fix)?.name?.trim() ?? fix;
        showInfo(`检测到当前输入文本为「${label}」`);
        return;
      }
      showWarning(langMatch.message);
    },
    [langOptions, modalDesc, showInfo, showWarning]
  );

  const handleGenerate = async () => {
    const description = voiceDescription.trim();
    const text = testText.trim();
    if (!description) {
      showError("请填写音色描述");
      return;
    }
    if (description.length > 500) {
      showError("音色描述不能超过 500 字符");
      return;
    }
    if (!text) {
      showError("请填写试听文本");
      return;
    }
    if (text.length > 500) {
      showError("试听文本不能超过 500 字符");
      return;
    }

    stopPreview();
    setGenerating(true);
    generateStartTimeRef.current = Date.now();
    window.setTimeout(() => {
      if (generateStartTimeRef.current && Date.now() - generateStartTimeRef.current >= 2000) {
        triggerMembershipRefresh();
      }
    }, 2000);
    try {
      const res = await postTimbreDesignGenerateApi({
        description,
        text,
        optimizeText: false,
      });
      if (res.code === 0 && Array.isArray(res.data)) {
        if (res.data.length === 0) {
          showError("未返回可用音色，请调整描述或试听文本后重试");
          return;
        }
        setGeneratedVoices(mapGenerateList(res.data, description));
        triggerPointsRefresh();
        if (!generateStartTimeRef.current || Date.now() - generateStartTimeRef.current >= 2000) {
          triggerMembershipRefresh();
        }
        setShowResults(true);
        setExamplesCollapsed(true);
        setSelectedTimbreId(null);
      } else if (res.msg) {
        showError(res.msg);
      }
    } catch (error) {
      console.error("生成音色失败:", error);
    } finally {
      setGenerating(false);
    }
  };
  const handleClear = () => {
    stopPreview();
    setVoiceDescription("");
    setTestText("");
    setShowResults(false);
    setGeneratedVoices([]);
    setSelectedTimbreId(null);
  };

  const handleApplyExample = (template: VoiceDesignTemplate) => {
    stopPreview();
    setVoiceDescription(template.voiceDescription);
    setTestText(template.previewText);
  };
  const handleConfirmChoice = () => {
    if (!selectedTimbreId) return;
    const selected = generatedVoices.find((v) => v.timbreId === selectedTimbreId);
    const origin = (selected?.originText ?? "").trim();
    // 弹窗展示生成接口返回的原文；若后端未带回则退回当前页面试听文本
    setModalDesc(origin || testText.trim());
    const detectedLang = findFirstResolvableLangValueMatchingText(origin || testText.trim(), langOptions);
    setModalLang(detectedLang || undefined);
    setModalGender(undefined);
    setModalAge(undefined);
    setRegisterOpen(true);
  };

  const closeRegister = () => {
    if (saveSubmitting) return;
    setRegisterOpen(false);
  };

  const handleSaveVoice = async () => {
    const missingFields: string[] = [];
    if (!voiceName.trim()) missingFields.push("音色名称");
    if (!modalLang) missingFields.push("语种");
    if (!modalGender) missingFields.push("性别");
    if (!modalAge) missingFields.push("年龄");
    if (!modalCategory) missingFields.push("类别");
    if (!modalDesc.trim()) missingFields.push("试听文本");
    if (missingFields.length > 0) {
      showError(`请填写：${missingFields.join("、")}`);
      return;
    }

    const modalText = modalDesc.trim();
    const modalLangCheck = validatePreviewTextForLang(modalText, modalLang!, langOptions);
    if (!modalLangCheck.ok) {
      showError(modalLangCheck.message);
      return;
    }

    const selected = generatedVoices.find((v) => v.timbreId === selectedTimbreId);
    if (!selected) {
      showError("未找到已选音色，请重新选择");
      return;
    }

    setSaveSubmitting(true);
    try {
      const textValue = modalDesc.trim();
      const originalFromGenerate = (selected.originText ?? "").trim();
      /** 未改原文则 text、originText 同传输入框内容；改过则 text 为输入框、originText 为生成返回原文 */
      const originTextValue =
        textValue === originalFromGenerate ? textValue : originalFromGenerate;

      const res = await postTimbreDesignSaveApi({
        voiceName: voiceName.trim(),
        description: voiceDescription.trim(),
        langCode: modalLang!,
        genderValue: modalGender!,
        ageValue: modalAge!,
        timbreValue: modalCategory!,
        pitchValue: "",
        styleValue: "",
        accentValue: "",
        text: textValue,
        originText: originTextValue,
        audioUrl: (selected.audioUrl || "").trim(),
      });
      if (res.code === 0) {
        showSuccess(res.msg || "保存成功");
        triggerPointsRefresh();
        triggerMembershipRefresh();
        setRegisterOpen(false);
        setVoiceName("");
        setModalLang(undefined);
        setModalGender(undefined);
        setModalAge(undefined);
        setModalCategory(undefined);
        setModalDesc("");
      }
    } catch (error) {
      console.error("保存音色失败:", error);
    } finally {
      setSaveSubmitting(false);
    }
  };

  const selectPopupContainer = (trigger: HTMLElement) =>
    (trigger?.closest(".auto-model-content") as HTMLElement | null) || document.body;

  return (
    <div className="voice-design-ws">
      <AlertMessage
        config={alertConfig}
        onClose={hideAlert}
        zIndex={ALERT_Z_INDEX.UPLOAD_VIDEO}
      />

      <div className="voice-design-ws__canvas">
        <section className="voice-design-ws__hero">
          <div className="voice-design-ws__kicker">Voice Design</div>
          <h1>音色设计</h1>
          <p>用自然语言描述你想要的声音特征，系统会生成多条候选音色供试听和登记。</p>
        </section>

        <section className="voice-design-ws__card voice-design-ws__section voice-design-ws__section--generate">
          <div className="voice-design-ws__section-head">
            <div className="voice-design-ws__section-icon" aria-hidden>
              <SparklesIcon className="voice-design-ws__icon" />
            </div>
            <div>
              <h2 className="voice-design-ws__section-title">AI 音色生成</h2>
              <p className="voice-design-ws__section-sub">输入音色描述，AI 为您创作独特音色</p>
            </div>
          </div>

          <div className="voice-design-ws__form-stack">
            <div className="voice-design-ws__field">
              <label className="voice-design-ws__label" htmlFor="voice-design-description">
                音色描述
              </label>
              <Input.TextArea
                id="voice-design-description"
                className="voice-design-ws__textarea"
                value={voiceDescription}
                onChange={(e) => setVoiceDescription(e.target.value)}
                placeholder="描述您想要的音色特点，如：一位年迈的老先生，说带北方口音的普通话，语速缓慢而沉稳，嗓音略带沙哑和沧桑感，仿佛一位饱经风霜的老爷爷在讲故事，充满岁月的智慧......"
                rows={4}
                maxLength={500}
                autoSize={false}
                style={{ resize: "none" }}
              />
              <span className="voice-design-ws__textarea-count">{voiceDescription.length} / 500</span>
            </div>
            <div className="voice-design-ws__field">
              <label className="voice-design-ws__label" htmlFor="voice-design-test">
                试听文本
              </label>
              <Input.TextArea
                id="voice-design-test"
                className="voice-design-ws__textarea"
                value={testText}
                onChange={(e) => setTestText(e.target.value)}
                placeholder="输入用于试听音色的文本"
                rows={4}
                maxLength={500}
                autoSize={false}
                style={{ resize: "none" }}
              />
              <span className="voice-design-ws__textarea-count">{testText.length} / 500</span>
            </div>
            <div className="voice-design-ws__control-row">
              <div className="voice-design-ws__polish-control" />
              <div className="voice-design-ws__actions">
                {hasPoints && Boolean(voiceDescription.trim()) && (
                  <span className="voice-design-ws__cost-estimate" title="按生成次数扣除">
                    <CoinsEstimateIcon />预计扣除 2 积分
                  </span>
                )}
                <button
                  type="button"
                  className="voice-design-ws__btn voice-design-ws__btn--primary"
                  onClick={() => void handleGenerate()}
                  disabled={generating}
                  aria-busy={generating}
                >
                  {generating ? (
                    <LoadingOutlined className="voice-design-ws__icon" spin />
                  ) : (
                    <SparklesIcon className="voice-design-ws__icon" />
                  )}
                  {showResults ? "重新生成" : "生成音色"}
                </button>
                <button
                  type="button"
                  className="voice-design-ws__btn voice-design-ws__btn--secondary"
                  onClick={handleClear}
                  disabled={generating}
                >
                  清空
                </button>
              </div>
            </div>
          </div>
        </section>

        {showResults ? (
          <section className="voice-design-ws__card voice-design-ws__section voice-design-ws__section--results">
            <div className="voice-design-ws__results-head">
              <div>
                <h2 className="voice-design-ws__results-title">生成结果</h2>
                <p className="voice-design-ws__results-hint">点击卡片选择音色，然后登记到音色库</p>
              </div>
              <button
                type="button"
                className="voice-design-ws__btn voice-design-ws__btn--secondary voice-design-ws__btn--regenerate"
                onClick={() => void handleGenerate()}
                disabled={generating}
              >
                {generating ? <LoadingOutlined className="voice-design-ws__icon" spin /> : <RefreshCwIcon className="voice-design-ws__icon" />}
                重新生成
              </button>
            </div>

            <div className="voice-design-ws__grid">
              {generatedVoices.map((v) => {
                const selected = selectedTimbreId === v.timbreId;
                const playing = playingTimbreId === v.timbreId;
                return (
                  <div
                    key={v.timbreId}
                    role="button"
                    tabIndex={0}
                    className={[
                      "voice-design-ws__voice-card",
                      selected ? "voice-design-ws__voice-card--selected" : "",
                      playing ? "voice-design-ws__voice-card--playing" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => setSelectedTimbreId(v.timbreId)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedTimbreId(v.timbreId);
                      }
                    }}
                  >
                    <div className="voice-design-ws__voice-card-head">
                      <div className="voice-design-ws__voice-title-row">
                        <span className="voice-design-ws__voice-name" title={v.indexLabel}>{v.indexLabel}</span>
                        <span className="voice-design-ws__voice-dur">{v.durationLabel}</span>
                      </div>
                      <span className={`voice-design-ws__checkbox${selected ? " voice-design-ws__checkbox--checked" : ""}`} aria-hidden>
                        <CheckboxCheckIcon className="voice-design-ws__checkbox-icon" />
                      </span>
                    </div>

                    <div className="voice-design-ws__voice-player-row">
                      <button
                        type="button"
                        className="voice-design-ws__play"
                        aria-label={playing ? "停止试听" : "播放试听"}
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePlayPreview(v);
                        }}
                      >
                        {playing ? (
                          <VoicePreviewPauseIcon className="voice-design-ws__play-icon" />
                        ) : (
                          <VoicePreviewPlayIcon className="voice-design-ws__play-icon voice-design-ws__play-icon--triangle" />
                        )}
                      </button>
                      <div className="voice-design-ws__wave" aria-hidden>
                        {v.wave.slice(0, 18).map((h, i) => (
                          <span key={i} className="voice-design-ws__wave-bar" style={{ height: Math.max(6, Math.min(22, h)) }} />
                        ))}
                      </div>
                      <button
                        type="button"
                        className="voice-design-ws__download-btn"
                        title="下载音色"
                        aria-label="下载音色"
                        onClick={(e) => handleDownload(e, v)}
                      >
                        <DownloadIcon className="voice-design-ws__download-icon" />
                      </button>
                    </div>
                    <div className="voice-design-ws__progress" aria-hidden>
                      <span className="voice-design-ws__progress-bar" />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="voice-design-ws__footer-bar">
              <p className="voice-design-ws__picked-line">
                已选择：
                <span className="voice-design-ws__picked-name" title={selectedLabel}>
                  {selectedLabel}
                </span>
              </p>
              <button
                type="button"
                className="voice-design-ws__btn voice-design-ws__btn--primary voice-design-ws__btn--register"
                disabled={!selectedTimbreId}
                onClick={handleConfirmChoice}
              >
                <CheckCircleIcon className="voice-design-ws__icon" />
                登记音色
              </button>
            </div>
          </section>
        ) : null}
        <section className="voice-design-ws__examples">
          <button
            type="button"
            className="voice-design-ws__examples-toggle"
            onClick={() => setExamplesCollapsed((v) => !v)}
            aria-expanded={!examplesCollapsed}
          >
            <span className="voice-design-ws__examples-title-wrap">
              <span className="voice-design-ws__examples-title-icon" aria-hidden>
                <LightbulbIcon className="voice-design-ws__example-lucide" />
              </span>
              <span className="voice-design-ws__examples-title">示例参考</span>
              <span className="voice-design-ws__examples-hint">— 点击示例可快速填入上方输入框</span>
            </span>
            <span className="voice-design-ws__examples-state">
              {examplesCollapsed ? "展开" : "收起"}
              <ChevronDownIcon className={`voice-design-ws__example-chevron${examplesCollapsed ? "" : " voice-design-ws__example-chevron--open"}`} />
            </span>
          </button>

          {!examplesCollapsed ? (
            <div className="voice-design-ws__examples-grid">
              {voiceTemplates.map((example) => (
                <button
                  key={example.id}
                  type="button"
                  className="voice-design-ws__example-card"
                  onClick={() => handleApplyExample(example)}
                >
                  <span className="voice-design-ws__example-card-icon" aria-hidden>
                    {example.icon === "book" ? (
                      <BookOpenIcon className="voice-design-ws__example-lucide" />
                    ) : example.icon === "megaphone" ? (
                      <MegaphoneIcon className="voice-design-ws__example-lucide" />
                    ) : (
                      <SparklesIcon className="voice-design-ws__example-lucide" />
                    )}
                  </span>
                  <span className="voice-design-ws__example-title">{example.title}</span>
                  <span className="voice-design-ws__example-summary">{example.description}</span>
                </button>
              ))}
            </div>
          ) : null}
        </section>
      </div>
      <AutoModel
        open={registerOpen}
        onClose={closeRegister}
        title="音色登记"
        size="md"
        contentClassName="voice-design-ws__register-modal"
        footerRight={<>
          <Button onClick={closeRegister} type="default" disabled={saveSubmitting}>
            取消
          </Button>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={() => void handleSaveVoice()}
            loading={saveSubmitting}
          >
            保存音色
          </Button>
        </>}
      >
        <div className="voice-design-ws__modal-body">
          <div>
            <label className="voice-design-ws__label">
              音色名称 <span className="voice-design-ws__req">*</span>
            </label>
            <Input
              className="voice-design-ws__input"
              value={voiceName}
              onChange={(e) => setVoiceName(e.target.value)}
              placeholder="给音色起个名字，如：纪录片旁白A"
            />
          </div>
          <div>
            <label className="voice-design-ws__label">
              语种 <span className="voice-design-ws__req">*</span>
            </label>
            <Select
              className="voice-design-ws__select"
              placeholder="选择语种"
              options={dictToSelectOptions(langOptions)}
              value={modalLang}
              onChange={handleModalLangChange}
              allowClear
              getPopupContainer={selectPopupContainer}
            />
          </div>
          <div>
            <label className="voice-design-ws__label">
              性别 <span className="voice-design-ws__req">*</span>
            </label>
            <Select
              className="voice-design-ws__select"
              placeholder="选择性别"
              options={dictToSelectOptions(genderOptions)}
              value={modalGender}
              onChange={setModalGender}
              allowClear
              getPopupContainer={selectPopupContainer}
            />
          </div>
          <div>
            <label className="voice-design-ws__label">
              年龄 <span className="voice-design-ws__req">*</span>
            </label>
            <Select
              className="voice-design-ws__select"
              placeholder="选择年龄段"
              options={dictToSelectOptions(ageOptions)}
              value={modalAge}
              onChange={setModalAge}
              allowClear
              getPopupContainer={selectPopupContainer}
            />
          </div>
          <div>
            <label className="voice-design-ws__label">
              类别 <span className="voice-design-ws__req">*</span>
            </label>
            <Select
              className="voice-design-ws__select"
              placeholder="选择音色类别"
              options={dictToSelectOptions(timbreOptions)}
              value={modalCategory}
              onChange={setModalCategory}
              allowClear
              getPopupContainer={selectPopupContainer}
            />
          </div>
          <div>
            <label className="voice-design-ws__label">
              试听文本 <span className="voice-design-ws__req">*</span>
            </label>
            <Input.TextArea
              className="voice-design-ws__textarea voice-design-ws__textarea--register"
              value={modalDesc}
              onChange={(e) => setModalDesc(e.target.value)}
              placeholder="请输入用于试听/克隆的文本"
              maxLength={500}
              rows={1}
            />
          </div>
        </div>
      </AutoModel>
    </div>
  );
};
